import React from 'react';
import { ArrayFieldTemplateProps, ArrayFieldTemplateItemType } from '@rjsf/utils';
import { useTranslation } from 'react-i18next';
import { ArrowUp, ArrowDown, Delete } from '../ProjectIcons';
import { buttonClasses } from '../tailwindClassnames';

// Template for rjsf-rendered arrays (arrays of objects — see generateUISchema's
// hasScalarItems gate; scalar arrays go through CustomArrayField instead).
// Lays the title and description full-width above the item list so nested
// arrays keep the whole card width instead of halving it at every level.
const CustomArrayFieldTemplate = (props: ArrayFieldTemplateProps) => {
    const { title, schema, items, canAdd, onAddClick, disabled, readonly, required } = props;
    const { t } = useTranslation();

    return (
        <div className="my-2 w-full">
            {(title || schema.description) && (
                <div className="flex flex-col mb-2">
                    {title && (
                        <label className="font-semibold">
                            {title}
                            {required && <span className="ml-1">*</span>}
                        </label>
                    )}
                    {schema.description && (
                        <span className="text-secondary-gray text-sm mt-1">{schema.description}</span>
                    )}
                </div>
            )}
            {items.map(({
                key,
                index,
                children,
                hasToolbar,
                hasMoveUp,
                hasMoveDown,
                hasRemove,
                onReorderClick,
                onDropIndexClick,
            }: ArrayFieldTemplateItemType) => (
                <div
                    key={key}
                    className="relative mb-3 border border-gray-lines rounded w-full p-3 transition-all duration-300"
                    data-testid={`CustomArrayFieldTemplate-item-${index}`}
                >
                    {hasToolbar && !readonly && (
                        <div className="absolute top-1 right-1 flex flex-row items-center gap-1 z-10">
                            {(hasMoveUp || hasMoveDown) && (
                                <>
                                    <button
                                        type="button"
                                        className="p-1 rounded hover:bg-light-gray-button disabled:opacity-30 disabled:cursor-not-allowed"
                                        title={t('Move up') || 'Move up'}
                                        aria-label={t('Move up') || 'Move up'}
                                        disabled={disabled || !hasMoveUp}
                                        onClick={onReorderClick(index, index - 1)}
                                    >
                                        <ArrowUp />
                                    </button>
                                    <button
                                        type="button"
                                        className="p-1 rounded hover:bg-light-gray-button disabled:opacity-30 disabled:cursor-not-allowed"
                                        title={t('Move down') || 'Move down'}
                                        aria-label={t('Move down') || 'Move down'}
                                        disabled={disabled || !hasMoveDown}
                                        onClick={onReorderClick(index, index + 1)}
                                    >
                                        <ArrowDown />
                                    </button>
                                </>
                            )}
                            {hasRemove && (
                                <button
                                    type="button"
                                    className="p-1 rounded hover:bg-light-gray-button disabled:opacity-30 disabled:cursor-not-allowed"
                                    title={t('Remove') || 'Remove'}
                                    aria-label={t('Remove') || 'Remove'}
                                    disabled={disabled}
                                    onClick={onDropIndexClick(index)}
                                >
                                    <Delete />
                                </button>
                            )}
                        </div>
                    )}
                    {children}
                </div>
            ))}
            {canAdd && !readonly && (
                <div className="flex justify-end mt-2">
                    <button
                        type="button"
                        className={buttonClasses.lightGreen}
                        disabled={disabled}
                        onClick={onAddClick}
                        data-testid="CustomArrayFieldTemplate-add-button"
                    >
                        {t('Add Item')}
                    </button>
                </div>
            )}
        </div>
    );
};

export default CustomArrayFieldTemplate;
